import {
  Box,
  Button,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  type Omittable,
} from "@salestrak/ui";

import {
  SettingsGroup,
  SettingsGroupRow,
} from "#app/components/settings/settings-group";

export type DangerZoneConfig = Omittable<{
  id: string;
  label: string;
  /** Muted second line under the label — what the action does to the data. */
  subtitle?: string;
  buttonLabel: string;
  /** `destructive` = danger-ghost ink; `default` = plain ghost, for the
   * non-destructive actions that share the group (resend invite,
   * reactivate). */
  variant: "destructive" | "default";
  onClick?: () => void;
  disabled?: boolean;
  /** Why the action is unavailable, one short clause ("Set another stage as
   * default first"). Shown as a tooltip on the disabled button — a disabled
   * control that cannot say why reads as broken. */
  disabledReason?: string;
}>;

/**
 * THE settings danger-zone group — every "Danger zone" section (profile,
 * member edit, team, workflow, stage, issue type) renders through this so the
 * grammar stays one thing: label + optional subtitle on the left, a quiet
 * ghost action on the right. Destructive actions wear the destructive ghost
 * (red ink at rest, red-tinted wash on hover) — never an outline or filled
 * chrome at rest.
 */
export function DangerZoneGroup({
  label = "Danger zone",
  dangerZone,
}: Omittable<{ label?: string; dangerZone: DangerZoneConfig[] }>) {
  if (dangerZone.length === 0) return null;
  return (
    <SettingsGroup label={label}>
      {dangerZone.map((action) => {
        const disabled = action.disabled ?? !action.onClick;
        const button = (
          <Button
            disabled={disabled}
            onClick={action.onClick}
            size="sm"
            color={action.variant === "destructive" ? "destructive" : "neutral"}
            variant="ghost"
          >
            {action.buttonLabel}
          </Button>
        );
        return (
          <SettingsGroupRow
            key={action.id}
            label={action.label}
            subtitle={action.subtitle}
          >
            <Box direction="row" justify="end">
              {disabled && action.disabledReason != null ? (
                // A disabled Button is pointer-events-none, so the hover that
                // opens the tooltip lands on this wrapper instead — which is
                // also why the wrapper carries the not-allowed cursor.
                <Tooltip>
                  <TooltipTrigger
                    render={<Box cursor="not-allowed" direction="row" />}
                  >
                    {button}
                  </TooltipTrigger>
                  <TooltipPopup side="top">
                    {action.disabledReason}
                  </TooltipPopup>
                </Tooltip>
              ) : (
                button
              )}
            </Box>
          </SettingsGroupRow>
        );
      })}
    </SettingsGroup>
  );
}
